import { FC, useCallback, useState } from 'react';

import { Drawer } from '@/components/common';
import { RadioPaymentSelector } from '@/components/subflow-v3/components/RadioPaymentSelector';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';

import { useStepTracking } from '@/hooks/subFlowAnalytics';
import { PaymentOption } from '@/hooks/usePaymentOptions';
import { Referrers, useStartCheckout } from '@/hooks/useStartCheckout';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';

import { TypeSubFlowV3Translations } from '@/utils/translations/TypeSubFlowV3Translations';

const DRAWER_EVENT_DATA = {
  content: 'Payment Options',
  contentType: 'Play Kit Subscription',
};

export interface PlayKitPaymentOptionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  t: TypeSubFlowV3Translations;
}

export const PlayKitPaymentOptionDrawer: FC<
  PlayKitPaymentOptionDrawerProps
> = ({ isOpen, onClose, t }) => {
  const [isLoading, setIsLoading] = useState(false);

  const { data, api } = useSubFlowV3Data();

  const { continueToCheckout, isCheckoutLoading } = useStartCheckout(
    t,
    'v3',
    Referrers.Cart,
  );

  const { onStepViewed } = useStepTracking('v3');

  const finishStep = useCallback(
    async (selectedPaymentOptionOverride: PaymentOption) => {
      setIsLoading(true);

      trackItemClickedEvent({
        url: window.location.href,
        type: CLICKED_ITEM_TYPES.BUTTON,
        pageLocation: PAGE_LOCATIONS.SUBSCRIPTION_FLOW.ORDER_REVIEW,
        cta: CTA_NAMES.CONTINUE_TO_CHECKOUT,
      });

      await continueToCheckout({
        callback: () => {
          setIsLoading(false);
        },
        selectedPaymentOptionOverride,
        shouldOmitDob: true,
        shouldIgnoreOnFlowComplete: true,
      });
    },
    [continueToCheckout],
  );

  const handleSubmit = useCallback(
    (paymentOption?: PaymentOption | null) => {
      if (!paymentOption) return;

      api.selectPaymentOption(paymentOption);

      finishStep(paymentOption);
    },

    [api, finishStep],
  );

  return (
    <Drawer
      open={isOpen}
      onOpen={() => onStepViewed(data)}
      onClose={onClose}
      title={t.paymentDrawerTitle}
      eventData={DRAWER_EVENT_DATA}
      customClassNames="mb-24"
    >
      <RadioPaymentSelector
        data={data}
        api={api}
        t={t}
        handleSubmit={handleSubmit}
        loadingCheckout={isCheckoutLoading || isLoading}
        drawerStyles
      />
    </Drawer>
  );
};
